import React from 'react';
import { Tooltip } from 'reactstrap';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import AccountStarIcon from 'mdi-react/AccountStarIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import ChartTimelineVariantIcon from 'mdi-react/ChartTimelineVariantIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import AppleIcon from 'mdi-react/AppleIcon';
import AndroidIcon from 'mdi-react/AndroidIcon';
import WebIcon from 'mdi-react/WebIcon';
import DevicesIcon from 'mdi-react/DevicesIcon';
import skeletonTableLoader from '../../../shared/components/custom/SkeletonLoaders';
import { partial } from '../../../util/translation/translation';

const CellTooltip = ({ id, text }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <InformationOutlineIcon
        id={id}
        size={14}
        className="text-muted"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          cursor: 'pointer',
        }}
      />
      <Tooltip
        placement="top"
        target={id}
        isOpen={isOpen}
        toggle={() => setIsOpen(!isOpen)}
        innerClassName="tooltip-black"
      >
        {text}
      </Tooltip>
    </>
  );
};

const platformIcon = (os) => {
  const normalized = (os || '').toLowerCase();
  if (normalized.includes('ios') || normalized.includes('apple'))
    return AppleIcon;
  if (normalized.includes('android')) return AndroidIcon;
  if (normalized.includes('web')) return WebIcon;
  return DevicesIcon;
};

// Fluid-width stat cell - DisplayCounter's fixed pixel tiles don't fit three
// across in this narrower column (they either wrap oddly or overflow the
// card), so these share the card's actual width via a CSS grid instead.
const StatCell = ({ id, icon: Icon, title, value, tooltip, isLast }) => (
  <div
    style={{
      position: 'relative',
      minWidth: 0,
      paddingRight: isLast ? 0 : 12,
      borderRight: isLast ? 'none' : '1px solid #eee',
    }}
  >
    {tooltip && <CellTooltip id={`${id}-tooltip`} text={tooltip} />}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: '#eef2fb',
        marginBottom: 6,
      }}
    >
      <Icon size={15} color="#3b6cf6" />
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </div>
    <small className="text-muted" style={{ wordBreak: 'break-word' }}>
      {title}
    </small>
  </div>
);

const StatsCards = ({
  totalSessions,
  sessionsPerUser,
  averageEngagementTime,
  sessionDataLoading,
  userAvgDataLoading,
  loginData,
  devices,
  peakActiveUsers,
  sumOfDailyAveragesEngagement,
}) => {
  const a = partial('Analytics');
  const isLoading = sessionDataLoading || userAvgDataLoading;
  const hasData = loginData.length > 0;

  return (
    <div
      style={{
        padding: '16px 16px 10px',
        marginBottom: 10,
        background: '#fff',
        borderRadius: 6,
        boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
      }}
    >
      <small className="text-muted d-block mb-2">{a('userActivity')}</small>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center">
          {skeletonTableLoader(1, 1, 70)}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          <StatCell
            id="stat-sessions"
            icon={AccountMultipleIcon}
            title={a('sessions')}
            value={hasData ? totalSessions.toLocaleString() : '0'}
          />
          <StatCell
            id="stat-sessions-per-user"
            icon={AccountGroupIcon}
            title={a('sessionsPerUser')}
            value={hasData ? `${sessionsPerUser}` : '0'}
          />
          <StatCell
            id="stat-avg-engagement"
            icon={ClockOutlineIcon}
            title={a('averageEngagementTimePerActiveUser')}
            value={hasData ? averageEngagementTime : '0'}
            tooltip={a('averageEngagementTimePerActiveUserTooltip')}
            isLast
          />
        </div>
      )}

      <hr className="mt-1 mb-2" />
      <small className="text-muted d-block mb-2">{a('insights')}</small>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center">
          {skeletonTableLoader(1, 1, 70)}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}
        >
          <StatCell
            id="stat-peak-active-users"
            icon={AccountStarIcon}
            title={a('peakActiveUsers')}
            value={hasData ? peakActiveUsers : '0'}
            tooltip={a('peakActiveUsersTooltip')}
          />
          <StatCell
            id="stat-sum-of-daily-averages"
            icon={ChartTimelineVariantIcon}
            title={a('sumOfDailyAverages')}
            value={hasData ? sumOfDailyAveragesEngagement : '0'}
            tooltip={a('sumOfDailyAveragesTooltip')}
            isLast
          />
        </div>
      )}

      <hr className="mt-1 mb-2" />
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
        <small className="text-muted mb-0">{a('streamName')}</small>
        {sessionDataLoading ? (
          skeletonTableLoader(1, 1, 70)
        ) : loginData.length === 0 ? (
          <span className="text-muted">{a('noData')}</span>
        ) : (
          (() => {
            const total = devices.reduce(
              (sum, device) => sum + (device.uniqueUserCount || 0),
              0
            );

            return devices.map((device) => {
              const Icon = platformIcon(device.os);
              const percent = total
                ? Math.round((device.uniqueUserCount / total) * 100)
                : 0;

              return (
                <div
                  key={device.os}
                  className="d-flex align-items-center"
                  style={{ gap: 6 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: '#eef2fb',
                    }}
                  >
                    <Icon size={13} color="#3b6cf6" />
                  </div>
                  <span>{device.os}</span>
                  <span style={{ fontWeight: 600 }}>{percent}%</span>
                </div>
              );
            });
          })()
        )}
      </div>
    </div>
  );
};

export default StatsCards;
